import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { deepPurple } from "@mui/material/colors";
import { AgBarSeriesOptions, AgChartOptions } from "ag-charts-community";
import { AgCharts as AgChartsComponent } from "ag-charts-react";
import chroma from "chroma-js";

import { Bin, calculateLinearBins, calculateLogBins, Scale } from "fond/utils/bucket";
import { formatCurrency } from "fond/utils/currency";

const BIN_COUNT = 50;
const AXIS_TICK_COUNT = 6;
// When a bar is very small, the border/corner-radius can swallow the fill entirely, making it
// invisible. To avoid this, we floor any non-empty bar to at least this fraction of the tallest
// bar's height.
const MIN_DISPLAY_RATIO = 0.08;

interface CostBin extends Bin {
  color: string;
}

interface CostDistributionChartProps {
  costs: number[];
  ctsTypeLabel: string | undefined;
  scale?: Scale;
}

// Assigns a color to each bin, from light to dark purple.
const colorBins = (bins: Bin[]): CostBin[] => {
  const colors = chroma.scale([deepPurple[100], deepPurple[900]]).colors(bins.length);
  return bins.map((bin, index) => ({ ...bin, color: colors[index] }));
};

const CostDistributionChart: React.FC<CostDistributionChartProps> = ({ costs, ctsTypeLabel, scale = "linear" }) => {
  const chartOptions: AgChartOptions = useMemo(() => {
    const logBins = scale === "log" ? calculateLogBins(costs, BIN_COUNT) : null;
    const useLog = logBins !== null;
    const bins = colorBins(logBins ?? calculateLinearBins(costs, BIN_COUNT));

    const maxCount = Math.max(1, ...bins.map(({ count }) => count));

    const toAxisValue = (realCost: number) => (useLog ? Math.log10(realCost) : realCost);
    const axisMin = bins.length > 0 ? toAxisValue(bins[0].rangeStart) : undefined;
    const axisMax = bins.length > 0 ? toAxisValue(bins[bins.length - 1].rangeEnd) : undefined;
    const axisTickValues =
      axisMin !== undefined && axisMax !== undefined
        ? Array.from({ length: AXIS_TICK_COUNT }, (_, index) => axisMin + (index / (AXIS_TICK_COUNT - 1)) * (axisMax - axisMin))
        : undefined;

    const displayBins = bins.map((bin) => ({ ...bin, displayCount: bin.count > 0 ? Math.max(bin.count, maxCount * MIN_DISPLAY_RATIO) : 0 }));

    const renderTooltip = (params: { datum: CostBin }) => {
      const datum = params.datum as CostBin;
      return {
        heading: ctsTypeLabel,
        title: `${datum.count} service location${datum.count !== 1 ? "s" : ""} · ${formatCurrency(datum.rangeStart, { notation: "compact" })} - ${formatCurrency(datum.rangeEnd, { notation: "compact" })}`,
        data: [],
        symbol: { marker: { fill: datum.color, stroke: datum.color } },
      };
    };

    const series: AgBarSeriesOptions = {
      type: "bar",
      direction: "vertical",
      xKey: "plotValue",
      yKey: "displayCount",
      xName: ctsTypeLabel,
      stroke: "#fff",
      strokeWidth: 1,
      cornerRadius: 3,
      itemStyler: (params) => ({ fill: (params.datum as CostBin).color }),
      tooltip: {
        range: "nearest",
        renderer: renderTooltip,
      },
    };

    return {
      data: displayBins,
      series: [series],
      // Left/right padding keeps the first and last axis labels from being clipped
      padding: { top: 0, bottom: 24, left: 24, right: 24 },
      axes: {
        x: {
          type: "number",
          position: "bottom",
          min: axisMin,
          max: axisMax,
          ...(axisTickValues ? { interval: { values: axisTickValues } } : {}),
          label: {
            fontSize: 9,
            avoidCollisions: false,
            formatter: (params) => formatCurrency(useLog ? 10 ** params.value : params.value, { notation: "compact" }),
          },
          gridLine: { enabled: false },
        },
        y: {
          type: "number",
          position: "left",
          gridLine: { enabled: false },
          line: { enabled: false },
          tick: { enabled: false },
          label: { enabled: false },
        },
      },
    };
  }, [costs, ctsTypeLabel, scale]);

  return (
    <Box flex={3} minHeight={0} display="flex" flexDirection="column">
      <Box display="flex" alignItems="center" justifyContent="space-between" p={1}>
        <Typography fontSize={12} fontWeight={700}>
          Distribution of service locations
        </Typography>
        <Typography fontSize={11} color="text.secondary">
          Hover on bars for detail
        </Typography>
      </Box>
      <Box flex={1} minHeight={0}>
        <AgChartsComponent options={chartOptions} style={{ height: "100%", width: "100%" }} />
      </Box>
    </Box>
  );
};

export default CostDistributionChart;
